import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { PostgreSqlLinkedService } from 'app/model/linkedservice/postgre-sql.linkedservice';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { EncryptionMethod, convertToEncryptionMethod, convertToValidateServerCertificate } from 'app/registries/consts/postgre-consts';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(PostgreSqlLinkedService),
    asynchronous: true,
    disabled: __IS_FABRIC_DPE__,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                database: '*',
                username: '*',
                password: 'secret',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: PostgreSqlLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class PostgreSqlLinkedServiceAdapter extends LinkedServiceAdapter<PostgreSqlLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => PostgreSqlLinkedService {
        return PostgreSqlLinkedService;
    }

    protected model(): IModel {
        return PostgreSqlLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: PostgreSqlLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.PostgreSqlLinkedService;
        properties.type = Metamodel.modelFor(PostgreSqlLinkedService).name;

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Host', connectionStringPartToResource(linkedService, PostgreSqlLinkedService.SERVER_NAME));
            builder.add('Port', connectionStringPartToResource(linkedService, PostgreSqlLinkedService.PORT));
            builder.add('Database', connectionStringPartToResource(linkedService, PostgreSqlLinkedService.DATABASE_NAME));
            builder.add('UID', connectionStringPartToResource(linkedService, PostgreSqlLinkedService.USER_NAME));

            builder.add('EncryptionMethod', linkedService.encryptionMethod ? linkedService.encryptionMethod.toString() : '0');
            if (linkedService.encryptionMethod !== EncryptionMethod.NoEncryption) {
                builder.add('ValidateServerCertificate', linkedService.validateServerCertificate ? '1' : '0');
            }
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('Password', connectionStringPartToResource(linkedService, PostgreSqlLinkedService.PASSWORD));
            }

            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.database === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        properties.typeProperties = {
            connectionString,
            password: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: PostgreSqlLinkedService): Promise<PostgreSqlLinkedService> {
        const prop = linkedServiceResource.properties as Model.PostgreSqlLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.MySqlLinkedServiceTypeProperties = prop.typeProperties || {} as any;

        // Cleanup, in case non of the if-else statement got executed
        linkedService.server = undefined;
        linkedService.database = undefined;
        linkedService.userName = undefined;
        linkedService.password = undefined;

        if (typeProperties.connectionString) {
            if (isString(typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.parse(typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, PostgreSqlLinkedService.SERVER_NAME, builder.getValue('Host', false) || builder.getValue('Server', false) || builder.getValue('HostName', false) || '');
                connectionStringPartToModel(linkedService, PostgreSqlLinkedService.PORT, builder.getValue('Port', false) || builder.getValue('PortNumber', false) || undefined);
                connectionStringPartToModel(linkedService, PostgreSqlLinkedService.DATABASE_NAME, builder.getValue('Database', false) || builder.getValue('DB', false) || '');
                connectionStringPartToModel(linkedService, PostgreSqlLinkedService.USER_NAME, builder.getValue('UID', false) || builder.getValue('LogonID', false) || '');

                linkedService.encryptionMethod = convertToEncryptionMethod(builder.getValue('EncryptionMethod', false) || builder.getValue('EM', false));
                linkedService.validateServerCertificate = linkedService.encryptionMethod !== EncryptionMethod.NoEncryption ?
                    convertToValidateServerCertificate(builder.getValue('ValidateServerCertificate', false)) : true;
                linkedService.password = Constants.maskedCredential;
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Host', 'Server', 'HostName', 'Port', 'PortNumber', 'Database', 'DB', 'UID', 'LogonID',
                    'EncryptionMethod', 'EM', 'ValidateServerCertificate', 'PWD', 'Password']);
                if (typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, PostgreSqlLinkedService.PASSWORD, typeProperties.password, this.context);
                }
            } else if (SecureStringReplacer.isSecureStringObject(typeProperties.connectionString)) {
                linkedService.server = Constants.maskedCredential;
                linkedService.database = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                if (typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, PostgreSqlLinkedService.PASSWORD, typeProperties.password, this.context);
                }
            } else if (typeProperties.connectionString && (typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKV = akvToModel(typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
                linkedService.enableAKV = true;
            }
        } else {
            // handle legacy property bag
            /* eslint-disable @typescript-eslint/no-explicit-any */
            linkedService.server = (typeProperties as any).server;
            linkedService.database = (typeProperties as any).database;
            linkedService.userName = (typeProperties as any).username;
            linkedService.password = Constants.maskedCredential;
            /* eslint-enable @typescript-eslint/no-explicit-any */
        }

        return linkedService;
    }
}
